import { Link } from "react-router-dom";
import {
  ArrowRight,
  Zap,
  BarChart3,
  Users,
  Layers,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MarketingLayout } from "@/components/marketing";
import { SEOHead } from "@/components/SEOHead";
import { HeroFlowDiagram } from "@/components/marketing/HeroFlowDiagram";
import { OldWayVsSidestep } from "@/components/marketing/OldWayVsSidestep";
import { CapabilityCarousel } from "@/components/marketing/CapabilityCarousel";
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  FadeIn,
} from "@/components/AnimatedSection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buildTrialSignupUrl, PLANS, TRIAL_SHORT } from "@/lib/pricing-config";
import { trackCTAClick } from "@/lib/gtm";
import { FEATURE_COUNT, HOME_AUDIENCES, HOME_FAQ_ITEMS, HOME_STEPS } from "@/lib/route-content";

// FAQ items, capability-card copy and the Organization / SoftwareApplication / FAQPage
// JSON-LD all live in src/lib/route-content.ts, which the build-time prerender
// bakes into this route's static HTML for non-JS crawlers. SEOHead resolves
// the JSON-LD from the same module, so nothing is passed here.
const faqItems = HOME_FAQ_ITEMS;

/** Lowest monthly list price, shown under the closing call to action. */
const STARTING_PRICE = Math.min(...PLANS.map((p) => p.monthlyPrice));

const AUDIENCE_ICONS: Record<string, LucideIcon> = {
  Agencies: Users,
  "Performance Marketers": BarChart3,
  "In-House Teams": Layers,
  "Founders & Operators": Zap,
};

const MarketingHome = () => {
  return (
    <MarketingLayout>
      <SEOHead />

      {/* ───────── HERO ───────── */}
      {/* theme-light: the hero uses the app's light palette ahead of the rest
          of the site; its bottom edge fades into the dark section below. */}
      <section className="theme-light relative overflow-hidden bg-background pt-14 pb-28 sm:pt-24 sm:pb-36 lg:pt-28 lg:pb-44">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_45%_at_50%_-5%,hsl(282_45%_60%/0.12),transparent_65%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_45%_40%_at_85%_70%,hsl(193_63%_58%/0.08),transparent_60%)]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 sm:h-44 bg-[linear-gradient(to_bottom,transparent_0%,hsl(250_40%_88%/0.45)_30%,hsl(240_38%_30%/0.85)_70%,hsl(var(--canvas-dark))_100%)]" />

        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <FadeIn>
              <h1 className="text-[2rem] font-extrabold tracking-tight text-foreground sm:text-[3.5rem] lg:text-[4rem] leading-[1.1] sm:leading-[1.06] lg:leading-[1.04]">
                The Ad Management Platform Built for Speed
              </h1>
            </FadeIn>
            <FadeIn delay={0.12}>
              <p className="mt-5 sm:mt-8 text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Launch ads faster, monitor performance clearly, and optimize with AI grounded in your real account data.
              </p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="mt-8 sm:mt-11 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <a
                  href={buildTrialSignupUrl()}
                  onClick={() => trackCTAClick("start_free_trial", buildTrialSignupUrl(), { cta_location: "home_hero" })}
                  className="w-full sm:w-auto"
                >
                  <Button variant="gradient" size="lg" className="text-base px-8 py-5 sm:py-6 h-auto w-full sm:w-auto">
                    Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <Link to="/pricing" className="w-full sm:w-auto">
                  <Button variant="outline" size="lg" className="text-base px-8 py-5 sm:py-6 h-auto w-full sm:w-auto bg-card">
                    Compare Plans
                  </Button>
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={0.28}>
              <p className="mt-4 sm:mt-5 text-[11px] sm:text-sm text-muted-foreground">
                {TRIAL_SHORT} · No credit card required
              </p>
            </FadeIn>
          </div>

          {/* What Sidestep does, shown as a flow instead of a product screenshot */}
          <div className="mt-12 sm:mt-14">
            <HeroFlowDiagram />
          </div>
        </div>
      </section>

      {/* ───────── PAIN vs SOLUTION ───────── */}
      <AnimatedSection className="py-16 sm:py-36">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-10 sm:mb-24">
            <h2 className="text-2xl font-extrabold text-foreground sm:text-[2.75rem] lg:text-[3.25rem] tracking-tight leading-[1.08]">
              The old way vs. the Sidestep way
            </h2>
            <p className="mt-4 sm:mt-6 text-sm sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
              Running paid ads shouldn't require five tabs, three spreadsheets, and a separate AI tool.
            </p>
          </FadeIn>

          <OldWayVsSidestep />
        </div>
      </AnimatedSection>

      {/* ───────── CAPABILITIES ───────── */}
      <section className="section-light overflow-hidden py-16 sm:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-6 sm:mb-10">
            <h2 className="text-2xl font-extrabold text-foreground sm:text-[2.75rem] lg:text-[3.25rem] tracking-tight leading-[1.08]">
              One workspace for the whole job
            </h2>
            <p className="mt-4 sm:mt-6 text-sm sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
              Launch, manage, monitor, optimize and collaborate across Meta and Google Ads, without bouncing between tabs.
            </p>
          </FadeIn>

          {/* Card copy is shared with the crawler content in route-content.ts. */}
          <FadeIn delay={0.1}>
            <CapabilityCarousel />
          </FadeIn>

          <div className="mt-5 sm:mt-7 text-center">
            <Link
              to="/features"
              className="group inline-flex items-center gap-1.5 py-3 text-sm sm:text-base font-semibold text-foreground underline decoration-primary/40 decoration-2 underline-offset-[6px] transition-colors hover:decoration-primary"
            >
              Explore all {FEATURE_COUNT} features
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── WHO IT'S FOR ───────── */}
      <section className="py-16 sm:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl font-extrabold text-foreground sm:text-[2.75rem] tracking-tight">
              Built for teams who run paid ads
            </h2>
            <p className="mt-3 sm:mt-5 text-sm sm:text-lg text-muted-foreground max-w-lg mx-auto leading-relaxed">
              Whether you're a solo founder or a 50-person agency — Sidestep fits the way you actually work.
            </p>
          </FadeIn>
          {/* Copy is shared with the crawler content in route-content.ts. */}
          <StaggerContainer className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto" staggerDelay={0.08}>
            {HOME_AUDIENCES.map((item) => {
              const Icon = AUDIENCE_ICONS[item.title] ?? Users;
              return (
                <StaggerItem key={item.title}>
                  <Card className="h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-soft hover:border-primary/15">
                    <CardContent className="p-5 sm:p-6 pt-5 sm:pt-6 flex h-full flex-col">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-9 w-9 shrink-0 rounded-xl bg-gradient-primary flex items-center justify-center">
                          <Icon className="h-4 w-4 text-primary-foreground" />
                        </div>
                        <h3 className="text-[15px] font-bold text-foreground leading-tight">{item.title}</h3>
                      </div>
                      <p className="text-[13px] sm:text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                      <p className="mt-auto pt-4 text-[11px] font-semibold uppercase tracking-wider text-secondary">
                        {item.outcome}
                      </p>
                    </CardContent>
                  </Card>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ───────── FAQ / OBJECTION HANDLING ───────── */}
      <section className="section-light py-16 sm:py-36">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <FadeIn className="text-center mb-10 sm:mb-20">
              <h2 className="text-2xl font-extrabold text-foreground sm:text-[2.75rem] tracking-tight">
                Questions before you start
              </h2>
              <p className="mt-3 sm:mt-5 text-sm sm:text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
                Quick answers to the things most teams ask first.
              </p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="border border-border/30 rounded-2xl sm:rounded-[20px] px-4 sm:px-7 bg-card shadow-card transition-all duration-300 hover:border-primary/15"
                  >
                    <AccordionTrigger className="text-left font-semibold hover:no-underline text-[13px] sm:text-base py-4 sm:py-6">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent forceMount className="text-muted-foreground text-xs sm:text-sm leading-relaxed pb-4 sm:pb-6">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ───────── FINAL CTA ───────── */}
      <AnimatedSection className="relative py-20 sm:py-36 cta-glow overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_50%_50%,hsl(275_43%_43%/0.14),transparent_70%)]" />
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <FadeIn>
              <h2 className="text-2xl font-extrabold text-foreground sm:text-[2.25rem] lg:text-[3.25rem] tracking-tight leading-[1.08]">
                 Ready to manage ads without the chaos?
               </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="mt-5 sm:mt-7 text-sm sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
                Up and running in minutes, in three simple steps.
              </p>
            </FadeIn>
          </div>

          {/* How it works, folded into the close. Copy is shared with the
              crawler content in route-content.ts. */}
          <FadeIn delay={0.15}>
            <div className="relative mt-10 sm:mt-14 max-w-4xl mx-auto">
              <div
                aria-hidden
                className="hidden md:block absolute top-5 left-[16.67%] right-[16.67%] h-px bg-gradient-to-r from-secondary/40 via-primary/40 to-secondary/40"
              />
              <ol className="relative grid gap-6 sm:gap-8 md:grid-cols-3">
                {HOME_STEPS.map((step, i) => (
                  <li key={step.title} className="relative text-center">
                    <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gradient-primary text-sm font-bold text-primary-foreground shadow-soft">
                      {i + 1}
                    </span>
                    <h3 className="mt-3 sm:mt-4 text-base sm:text-lg font-bold text-foreground">{step.title}</h3>
                    <p className="mt-1.5 text-[13px] sm:text-sm text-muted-foreground leading-relaxed max-w-[16rem] mx-auto">
                      {step.desc}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </FadeIn>

          <div className="max-w-2xl mx-auto text-center">
            <FadeIn delay={0.2}>
              <div className="mt-10 sm:mt-14 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <a
                  href={buildTrialSignupUrl()}
                  onClick={() => trackCTAClick("start_free_trial", buildTrialSignupUrl(), { cta_location: "home_bottom" })}
                  className="w-full sm:w-auto"
                >
                  <Button variant="gradient" size="lg" className="text-sm sm:text-base px-8 sm:px-10 py-4 sm:py-6 h-auto w-full sm:w-auto">
                    Start Free Trial <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                </a>
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button variant="outline" size="lg" className="text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-6 h-auto w-full sm:w-auto">
                    Book an Agency Demo
                  </Button>
                </Link>
              </div>
              <p className="mt-4 sm:mt-5 text-[11px] sm:text-sm text-muted-foreground">
                {TRIAL_SHORT} · No credit card required · Plans from ${STARTING_PRICE}/month
              </p>
            </FadeIn>
          </div>
        </div>
      </AnimatedSection>
    </MarketingLayout>
  );
};

export default MarketingHome;
